import { SelectChangeEvent } from "@mui/material";
import { VideoEvent } from "../../../../../utilities/useTimestamps";
import i18n from "../../../../../../i18n";
import { CoreMultiSelect } from "@verity/components/core";
import { RestrictedDeviceLoggedEvent } from "@yujacorp/verity-api-contract";

type Filter = { [key: string]: boolean };

export const FILTER_EVENTS: {
	name: string;
	check: (event: VideoEvent) => boolean;
	type?: "flagEvent" | "sessionEvent";
}[] = [
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.allEvents"),
		check: () => true,
		type: "sessionEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.tabNotActive"),
		check: (event: VideoEvent) => event.type === "tab" && event.details?.focus === false,
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.copyandPaste"),
		check: (event: VideoEvent) => event.type === "clipboard" && event.name === "keypress",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.WebNav"),
		check: (event: VideoEvent) =>
			(event.type === "web navigation" && event.name === "webNavigation") ||
			(event.type === "web_navigation" && event.name === "web_navigation"),
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.screenshot"),
		check: (event: VideoEvent) => event.type === "screenshot" && event.name === "keypress",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.webcamAndAudio"),
		check: (event: VideoEvent) =>
			event.type === "interrupt" && event.name === "trigger" && event.details.type === "webcam",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.screenshareInterrupted"),
		check: (event: VideoEvent) =>
			event.type === "interrupt" && event.name === "trigger" && event.details.type === "display",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.multipleDisplays"),
		check: (event: VideoEvent) =>
			event.type === "interrupt" && event.name === "trigger" && event.details.type === "monitor count",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.faceNotDetected"),
		check: (event: VideoEvent) => {
			return (
				(event.type === "face detection" && event.name === "target" && event.details.count === 0) ||
				(event.type === "worker" && event.name === "face" && event.details.detections.length === 0) ||
				(event.type === "machine-learning" &&
					event.name === "face-detection" &&
					event.details.currentState === "no-faces")
			);
		},
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.multiplePeopleDetected"),
		check: (event: VideoEvent) => {
			return (
				(event.type === "face detection" && event.name === "other") ||
				(event.type === "worker" && event.name === "face" && event.details.detections.length > 1) ||
				(event.type === "machine-learning" &&
					event.name === "face-detection" &&
					event.details.currentState === "multiple-faces")
			);
		},
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.objectDetected"),
		check: (event: VideoEvent) => {
			return (
				event.type === "object detection" ||
				(event.type === "worker" && event.name === "object") ||
				(event.type === "machine-learning" &&
					event.name === "object-detection" &&
					event.details.currentState === "detected-cell-phone")
			);
		},
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.audioDetected"),
		check: (event: VideoEvent) =>
			event.type === "interrupt" && event.name === "trigger" && event.details.type === "speaking",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.blockedAppOpened"),
		check: (event: VideoEvent) =>
			event.type === "interrupt" && event.name === "trigger" && event.details.type === "opens_blocked_app",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.blockedExtensionEnabled"),
		check: (event: VideoEvent) =>
			event.type === "interrupt" && event.name === "trigger" && event.details.type === "enables_blocked_extension",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.handoffEnabled"),
		check: (event: VideoEvent) =>
			event.type === "interrupt" && event.name === "trigger" && event.details.type === "handoff",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.navBlocked"),
		check: (event: VideoEvent) => event.type === "web navigation" && event.name === "blocked navigation",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.sidePanelDetected"),
		check: (event: VideoEvent) => event.type === "user action" && event.name === "side panel detected",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.rightClickAttempt"),
		check: (event: VideoEvent) => event.type === "user action" && event.name === "right click blocked",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.printAttempt"),
		check: (event: VideoEvent) => event.type === "user action" && event.name === "printing blocked",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.visitedDecoySite"),
		check: (event: VideoEvent) => event.type === "user action" && event.name === "decoy site viewed",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.noSubmission"),
		check: (event: VideoEvent) => event.type === "session" && event.name === "end proctoring" && event.flag_level !== 0,
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.connectionInterrupted"),
		check: (event: VideoEvent) =>
			event.type === "system" && event.name === "connection status" && event.details.connected === false,
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.severalPeopleDetected"),
		check: (event: VideoEvent) => event.type === "face detection" && event.name === "other" && event.details.count > 1,
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.sessionStart"),
		check: (event: VideoEvent) => event.type === "session" && event.name === "start proctoring",
		type: "sessionEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.sessionEnd"),
		check: (event: VideoEvent) =>
			event.type === "session" && (event.name === "end proctoring" || event.name === "force stop proctoring"),
		type: "sessionEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.quizStart"),
		check: (event: VideoEvent) => event.type === "session" && event.name === "start quiz" && !event.details?.fromSetup,
		type: "sessionEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.quizEnd"),
		check: (event: VideoEvent) => event.type === "session" && event.name === "end quiz",
		type: "sessionEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.recordingStart"),
		check: (event: VideoEvent) => event.type === "session" && event.name === "start recording",
		type: "sessionEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.recordingEnd"),
		check: (event: VideoEvent) => event.type === "session" && event.name === "end recording",
		type: "sessionEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.uploadIncomplete"),
		check: (event: VideoEvent) => event.type === "session" && event.name === "upload incomplete",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.codeAutofill"),
		check: (event: VideoEvent) => event.type === "access code" && event.name === "autofill",
		type: "sessionEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.audioBridge"),
		check: (event: VideoEvent) => event.type === "live" && event.name === "audio bridge",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.live"),
		check: (event: VideoEvent) => event.type === "live",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.connectionStatus"),
		check: (event: VideoEvent) => event.type === "system" && event.name === "connection status",
		type: "sessionEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.blockAppsFailed"),
		check: (event: VideoEvent) =>
			event.type === "system" && event.name === "block applications" && !event.details.success,
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.faceIdMismatch"),
		check: (event: VideoEvent) => event.type === "id" && event.name === "face id mismatch",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.IdSkippedAi"),
		check: (event: VideoEvent) => event.type === "id" && event.name === "bypassed ai detection",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.recordAppsActivity"),
		check: (event: VideoEvent) => event.type === "system" && event.name === "applications activity",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.humanFlaggedEvent"),
		check: (event: VideoEvent) => event.type === "custom",
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.restrictedDevice"),
		check: (event: VideoEvent) => event.type === ("restricted-device" satisfies RestrictedDeviceLoggedEvent["type"]),
		type: "flagEvent",
	},
	{
		name: i18n.t("result.components.resultsPage.eventTypeSelectorTSX.flaggedKeyword"),
		check: (event: VideoEvent) => event.type === "machine-learning" && event.name === "flagged-keyword",
		type: "flagEvent",
	},
];

export default function EventTypeSelector({
	events,
	handleChange,
	filter,
}: {
	events: VideoEvent[];
	handleChange: (event: SelectChangeEvent<string[]>) => void;
	filter: Filter;
}) {
	const options = events.reduce(
		(acc, event) => {
			FILTER_EVENTS.forEach((option) => {
				if (option.check(event)) {
					acc[option.name] = filter[option.name];
				}
			});
			return acc;
		},
		{} as Record<string, boolean>,
	);

	// Create labels for each option
	const optionLabels = Object.keys(options).reduce(
		(acc, key) => {
			acc[key] = key;
			return acc;
		},
		{} as Record<string, string>,
	);

	return (
		<CoreMultiSelect
			options={options}
			optionLabels={optionLabels}
			onChange={handleChange}
			label={i18n.t("result.components.resultsPage.eventComboBoxName")}
			ariaLabel={i18n.t("result.components.resultsPage.ariaLabels.eventComboBox")}
			menuWidthCh={30}
			filter={(key) =>
				FILTER_EVENTS.some((ev) => ev.name === key && (ev.type === "flagEvent" || ev.type === "sessionEvent"))
			}
			sx={{ marginRight: "10px" }}
		/>
	);
}

export function getEventFilterCheckboxes(
	events: VideoEvent[],
	filter: Filter,
	setFilter: (nextFilter: Filter | ((prev: Filter) => Filter)) => void,
) {
	const options = events.reduce(
		(acc, event) => {
			FILTER_EVENTS.forEach((option) => {
				if (option.check(event)) {
					acc[option.name] = {
						checked: !!filter[option.name],
						children: option.name,
						onChange: (checked: boolean) => setFilter((prev) => ({ ...prev, [option.name]: checked })),
					};
				}
			});
			return acc;
		},
		{} as Record<string, { checked: boolean; children: string; onChange: (checked: boolean) => void }>,
	);

	return Object.values(options);
}
